import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { Link } from "wouter";
import { COLOR_LIST } from "@/data/colors";
import { understandingColorSections } from "@/data/understanding-color-sections";
import { prepareArticleMarkup } from "@/lib/color-reference";
import "./understanding-color-guide.css";

const linkedColors = [
  "Red",
  "Orange",
  "Yellow",
  "Green",
  "Blue",
  "Purple",
  "Pink",
  "Black",
  "White",
  "Beige",
]
  .map((name) => COLOR_LIST.find((color) => color.name.toLowerCase() === name.toLowerCase()))
  .filter((color) => color !== undefined);

function GuideArticleBody() {
  const [articleMarkup, setArticleMarkup] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const articleHtml = useMemo(() => ({ __html: articleMarkup ?? "" }), [articleMarkup]);
  const [pendingScrollTarget, setPendingScrollTarget] = useState<{
    id: string;
    behavior: ScrollBehavior;
  } | null>(null);
  const loaderRef = useRef<HTMLDivElement>(null);
  const loadPromiseRef = useRef<Promise<string> | null>(null);

  const loadArticle = useCallback(() => {
    if (!loadPromiseRef.current) {
      loadPromiseRef.current = import(
        "@assets/understanding_color_reference_1791934579970.html?raw"
      )
        .then(({ default: source }) => prepareArticleMarkup(source))
        .catch((error: unknown) => {
          console.error("Unable to load the color reference.", error);
          setLoadFailed(true);
          return "";
        });
    }
    return loadPromiseRef.current.then((markup) => {
      setArticleMarkup(markup);
      return markup;
    });
  }, []);

  useEffect(() => {
    const target = loaderRef.current;
    if (!target) return;
    if (!("IntersectionObserver" in window)) {
      void loadArticle();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          void loadArticle();
        }
      },
      { rootMargin: "1400px 0px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [loadArticle]);

  const scheduleFragmentTarget = useCallback(
    (hash: string, behavior: ScrollBehavior) => {
      const sectionId = hash.slice(1);
      if (
        sectionId !== "understanding-color-title" &&
        sectionId !== "color-library-links" &&
        !understandingColorSections.some(([id]) => id === sectionId)
      ) {
        return;
      }

      setPendingScrollTarget({ id: sectionId, behavior });
      if (understandingColorSections.some(([id]) => id === sectionId)) {
        void loadArticle();
      }
    },
    [loadArticle],
  );

  useEffect(() => {
    const handleHashChange = () => scheduleFragmentTarget(window.location.hash, "auto");
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [scheduleFragmentTarget]);

  useEffect(() => {
    if (!pendingScrollTarget) return;
    if (loadFailed) {
      setPendingScrollTarget(null);
      return;
    }
    if (
      understandingColorSections.some(([id]) => id === pendingScrollTarget.id) &&
      articleMarkup === null
    ) {
      return;
    }
    const target = document.getElementById(pendingScrollTarget.id);
    if (target) {
      target.setAttribute("tabindex", "-1");
      target.scrollIntoView({ behavior: pendingScrollTarget.behavior });
      const targetId = pendingScrollTarget.id;
      window.requestAnimationFrame(() => {
        const focusTarget = document.getElementById(targetId);
        if (!focusTarget) return;
        focusTarget.setAttribute("tabindex", "-1");
        focusTarget.focus({ preventScroll: true });
      });
    }
    setPendingScrollTarget(null);
  }, [articleMarkup, loadFailed, pendingScrollTarget]);

  const jumpToSection = async (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    setPendingScrollTarget({ id, behavior: "smooth" });
    await loadArticle();
    const nextUrl = `${window.location.pathname}${window.location.search}#${id}`;
    window.history.pushState(null, "", nextUrl);
  };

  const copySectionLink = async (event: MouseEvent<HTMLElement>) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest<HTMLButtonElement>("[data-section-link-id]");
    if (!button || !event.currentTarget.contains(button)) return;

    const sectionId = button.dataset.sectionLinkId;
    const section = understandingColorSections.find(([id]) => id === sectionId);
    const feedback = button.parentElement?.querySelector<HTMLElement>("[data-section-link-feedback]");
    if (!sectionId || !section || !feedback) return;

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard access is unavailable.");
      }
      const url = new URL("/", window.location.origin);
      url.hash = sectionId;
      await navigator.clipboard.writeText(url.toString());
      feedback.textContent = "Link copied. You can now share this section.";
    } catch {
      feedback.textContent = "Could not copy the link. Check clipboard permissions and try again.";
    }
  };

  return (
    <>
      <nav className="color-reference-toc" aria-label="Understanding Color guide contents">
        <h2>In this guide</h2>
        <ul>
          {understandingColorSections.map(([id, label]) => (
            <li key={id}>
              <a href={`#${id}`} onClick={(event) => void jumpToSection(event, id)}>
                {label}
              </a>
            </li>
          ))}
        </ul>
      </nav>
      <div
        ref={loaderRef}
        className="color-reference-article-loader"
        aria-busy={articleMarkup === null}
      >
        {articleMarkup !== null && !loadFailed ? (
          <article
            className="color-reference-content"
            onClick={copySectionLink}
            dangerouslySetInnerHTML={articleHtml}
          />
        ) : loadFailed ? (
          <p className="color-reference-loading" role="alert">
            The color reference could not be loaded. Refresh the page to try again.
          </p>
        ) : (
          <p className="color-reference-loading" role="status">
            Loading the full color reference…
          </p>
        )}
      </div>

      <section className="color-reference-library" aria-labelledby="color-library-links">
        <div>
          <p className="color-reference-eyebrow">Explore actual colors</p>
          <h2 id="color-library-links">Continue with the color library</h2>
          <p>
            Open a color profile for its shade, associations, psychology notes, and practical uses.
          </p>
        </div>
        <div className="color-reference-swatches">
          {linkedColors.map((color) => (
            <Link
              key={color.id}
              href={`/learn/${color.id}`}
              className="color-reference-swatch"
              aria-label={`Explore ${color.name} in the color library`}
            >
              <span style={{ backgroundColor: color.hex }} aria-hidden="true" />
              {color.name}
            </Link>
          ))}
        </div>
        <div className="color-reference-tools" aria-label="Related color tools">
          <Link href="/learn">Browse all 175 color profiles</Link>
          <Link href="/paint-visualizer">Try the Paint Visualizer</Link>
          <Link href="/color-sandbox">Test a color palette</Link>
          <Link href="/color-grabber">Extract colors from a photo</Link>
          <Link href="/insights">Read color design insights</Link>
        </div>
      </section>
    </>
  );
}

export function UnderstandingColorGuide() {
  return (
    <section className="color-reference" aria-labelledby="understanding-color-title">
      <header className="color-reference-heading">
        <p className="color-reference-eyebrow">A practical color reference</p>
        <h2 id="understanding-color-title">Understanding Color</h2>
        <p className="color-reference-intro">
          A practical guide to color theory, color families, psychology, cultural meaning, palette
          building, branding, and accessible design.
        </p>
      </header>
      <GuideArticleBody />
    </section>
  );
}
